import { Button, Flex, Input } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import config from '../../config';
import { REFERRAL_PUBLIC_TERMS } from '../../constants/urls';
import {
  useSendReferralLinkEmailLazyQuery,
  useSendReferralLinkSmsLazyQuery,
} from '../../gql/generated/graphql';
import { buildReferralUrl } from '../../utils/referrals';
import { validateEmail, validatePhoneInput } from '../../utils/validation/email';
import { Link } from '../ui/links/Link';

type ContactType = 'text' | 'email';

const SendToggleForm = ({
  code,
  firstName,
  lastName,
}: {
  code: string;
  firstName: string | null;
  lastName: string | null;
}) => {
  const [type, setType] = useState<ContactType>('text');
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [sent, setSent] = useState(false);

  const [sendEmailLink] = useSendReferralLinkEmailLazyQuery();

  const [sendSmsLink] = useSendReferralLinkSmsLazyQuery();

  const validateInput = (val: string) => {
    if (type === 'text') {
      return !!validatePhoneInput(val);
    }
    return !!validateEmail(val);
  };

  useEffect(() => {
    setIsValid(validateInput(value));
  }, [value, type]);

  useEffect(() => {
    if (!sent) return;

    const timer = setTimeout(() => {
      setSent(false);
    }, 3000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [sent]);

  const handleSendEmail = async () => {
    setSending(true);
    await sendEmailLink({
      variables: {
        email: value,
        url: `${buildReferralUrl({
          code,
        })}`,
        firstName: firstName || '',
        lastName: lastName || '',
      },
    });
    setSending(false);
    setSent(true);
    setValue('');
  };

  const handleSendSms = async () => {
    setSending(true);
    await sendSmsLink({
      variables: {
        phone: value.replace(/\D/g, ''),
        url: `${buildReferralUrl({
          code,
          utms: {
            medium: 'referral',
            campaign: 'invite-sms',
          },
        })}`,
        firstName: firstName || '',
        lastName: lastName || '',
      },
    });
    setSending(false);
    setSent(true);
    setValue('');
  };

  const handleSubmit = () => {
    if (!isValid) return;
    if (type === 'email') {
      handleSendEmail();
      return;
    }
    handleSendSms();
  };

  const handleToggle = (newType: ContactType) => {
    setType(newType);
    setValue(''); // Clear input on toggle change
    setError(false);
  };

  return (
    <Flex flexDir="column" gap={4}>
      {/* Toggle Buttons */}
      <Flex mt={-2} flexDir="row" justifyContent="center" fontSize={12} color="#888">
        Don't forget, there's no limit on how many times you can share your link.
      </Flex>
      <Flex flexDir="row" justifyContent="center">
        <Button
          borderRadius={24}
          bg={type === 'text' ? 'oceanBoatBlue' : '#eee'}
          color={type === 'text' ? 'white' : '#888'}
          _hover={{ opacity: 0.8 }}
          borderRightRadius={0}
          type="button"
          py={6}
          px={8}
          onClick={() => handleToggle('text')}
        >
          Text
        </Button>
        <Button
          type="button"
          borderRadius={24}
          borderLeftRadius={0}
          bg={type === 'email' ? 'oceanBoatBlue' : '#eee'}
          color={type === 'email' ? 'white' : '#888'}
          py={6}
          px={8}
          _hover={{ opacity: 0.8 }}
          onClick={() => handleToggle('email')}
        >
          Email
        </Button>
      </Flex>
      {sent && (
        <Flex w="100%" bg="leaseEndGreen" color="white" p={4} borderRadius={8}>
          Link {type === 'email' ? 'emailed' : 'texted'}!
        </Flex>
      )}
      {/* Single Input */}
      <Input
        type={type === 'text' ? 'tel' : 'email'}
        placeholder={type === 'text' ? 'Phone number' : 'Email'}
        value={value}
        p={8}
        onChange={(e) => {
          setValue(e.target.value);
          setError(false);
        }}
        borderColor={error ? 'red.500' : 'gray.200'}
      />

      {/* Submit Button */}
      <Button
        type="button"
        borderRadius={8}
        onClick={handleSubmit}
        isDisabled={!isValid || sending}
      >
        {sending ? 'Sending' : 'Send'}
      </Button>
      <Flex flexDir="row" justifyContent="center" fontSize={12} color="#888">
        By sharing your code/url through any medium you agree to the{' '}
        <Link
          letterSpacing={0}
          p={0}
          m={0}
          ms={1}
          color="oceanBoatBlue"
          fontSize={12}
          to={`${config.mktUrl}${REFERRAL_PUBLIC_TERMS}`}
          target="_blank"
          title="Lease Friends terms of service"
        >
          terms of service
        </Link>
        .
      </Flex>
    </Flex>
  );
};

export default SendToggleForm;
